import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from '../../components/SideBar/Sidebar';
import * as Body from './Styles';
import SearchIcon from '../../../assets/icons/Search.svg';
import AddIcon from '../../../assets/icons/add-icon.svg';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Loading from '../../components/Loading/Loading';
import Avatar from '../../../assets/images/avatar.png';
import * as Functions from '../../../utils/functions/Functions';
import * as defines from '../../../utils/defines/Defines';
import { State } from '../../redux/store';
import SendInvite from './Modals/SendInvite';

// Improved type definition
interface UserProps {
  idUsuario: string;
  nomeCompleto: string;
  fotoUsuario: string | null;
  tipoTectrol: number;
}

const TectrolUserList: React.FC = () => {
  // Selectors
  const userType = useSelector((state: State) => state.userType.userType);
  const acessLevel = useSelector((state: State) => state.acessLevel.acessLevel);

  // State management
  const [users, setUsers] = useState<UserProps[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserProps[]>([]);
  const [loading, setLoading] = useState(true);
  const [createUser, setCreateUser] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Cargo mapping with memoization
  const getCargo = useCallback((id: number) => {
    const cargoMap: Record<number, string> = {
      [defines.CargoTectrol['Comercial']]: 'Comercial',
      [defines.CargoTectrol['Engenheiro']]: 'Engenharia',
      [defines.CargoTectrol['Suporte']]: 'Suporte',
      [defines.CargoTectrol['Padrão']]: 'Padrão',
    };
    return cargoMap[id] || 'Cargo Indisponível';
  }, []);

  // Fetch users with improved error handling
  const fetchUsers = useCallback(() => {
    setLoading(true);
    axios.get(
      `${defines.apiURL}/api/TectrolCRUD?userTypeRequest=${userType}`,
      { withCredentials: true }
    )
    .then((response) => {
      // Filter out users with null or empty names, then sort
      const validUsers = response.data.content
        .filter((user: UserProps) => user.nomeCompleto?.trim())
        .sort((a: UserProps, b: UserProps) => 
          a.nomeCompleto.localeCompare(b.nomeCompleto)
        );

      setUsers(validUsers);
      setFilteredUsers(validUsers);
      setLoading(false);
    })
    .catch((error) => {
      setLoading(false);
      Functions.ResolveErrors(
        error.response.status,
        error.response.data.code,
        error.response.data.message,
        navigate,
        undefined,
        error.response.data.content
      );
    });
  }, [userType, navigate]);

  // Memoized filtering logic
  const filteredList = useMemo(() => {
    if (!searchTerm) return users;
    
    const normalizedSearch = searchTerm.toUpperCase().trim();
    return users.filter((user) => 
      user.nomeCompleto.toUpperCase().includes(normalizedSearch)
    );
  }, [users, searchTerm]);

  // User click handler
  const handleUserClick = useCallback((user: UserProps) => {
    navigate('/tectrol/editar', { state: user });
  }, [navigate]);

  // Initial users fetch
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Search input handler
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  return (
    <Body.Container>
      <Sidebar
        type={userType}
        content={defines.ContentSidebar.MANAGE_USERS}
        tituloPagina="Administradores"
      />
      
      <Body.SubTitle className="mt-2.5">
        Toque em um usuário para visualizar seus detalhes.
      </Body.SubTitle>
      
      <Body.Row className="w-full flex justify-between items-center mt-5">
        <Body.FindContent>
          <Body.Find
            placeholder="Buscar usuário"
            onChange={handleSearchChange}
            ref={inputRef}
            value={searchTerm}
          />
          <Body.Icon
            src={SearchIcon}
            className="cursor-text"
            onClick={() => inputRef.current?.focus()}
          />
        </Body.FindContent>
        
        {acessLevel === defines.CargoTectrol['Suporte'] && (
          <Body.HeaderButton onClick={() => setCreateUser(true)}>
            <Body.Icon
              src={AddIcon}
              className="relative top-0 left-0"
            />
          </Body.HeaderButton>
        )}
      </Body.Row>
      
      <Body.Scroll className="mt-5">
        {filteredList.length === 0 ? (
          <Body.Text>Nenhum usuário encontrado</Body.Text>
        ) : (
          filteredList.map((user) => (
            <Body.SelectUser
              key={user.idUsuario}
              onClick={() => handleUserClick(user)}
            >
              <div className="flex items-center gap-2.5">
                <Body.Photo src={user.fotoUsuario || Avatar} />
                <div className="flex flex-col justify-center">
                  <Body.Text>{user.nomeCompleto}</Body.Text>
                  <Body.NoteText>
                    {getCargo(user.tipoTectrol)}
                  </Body.NoteText>
                </div>
              </div>
            </Body.SelectUser>
          ))
        )}
      </Body.Scroll>

      {createUser && <SendInvite toggle={setCreateUser} />}
      {loading && <Loading />}
    </Body.Container>
  );
};

export default TectrolUserList;